"use client";
// components/MapFilterPanel.jsx

import { useEffect, useState, useCallback } from "react";
import DualRangeSlider from "./DualRangeSlider";
const CATEGORIES = [
  { id: "Art Supplies",             emoji: "🎨" },
  { id: "Books",                    emoji: "📚" },
  { id: "Classroom Basics",         emoji: "🏫" },
  { id: "Educational Games",        emoji: "🎮" },
  { id: "Food, Clothing & Hygiene", emoji: "🍎" },
  { id: "Instructional Technology", emoji: "🔌" },
  { id: "STEM",                     emoji: "🔬" },
  { id: "Decor",                    emoji: "🌟" },
  { id: "Furniture",                emoji: "🪑" },
  { id: "Other",                    emoji: "📦" },
];


const GRADE_GROUPS = [
  { label: "Pre-K – K", grades: ["PK","K", "PreK","PreK-5"]                  },
  { label: "1 – 5",     grades: ["1","2","3","4","5","K-5","PreK-5","3,5","K-8","5-8"]},
  { label: "6 – 8",     grades: ["6","7","8","6,7,8","6-8","K-8","5-8"]},
  { label: "9 – 12",    grades: ["9","10","11","12", "9-12", "9 - 12"]},
];

// ── Defaults at ceiling so nothing is filtered on load ────────────────────────
export const DEFAULT_FILTERS = {
  minValue:     0,
  maxValue:     500,    // slider max — API treats 500 as 500+
  minNeeded:    1,
  maxNeeded:    100,    // high ceiling
  itemMinPrice: 0,
  itemMaxPrice: 200,    // high ceiling
  minEnrollment:   0,
  maxEnrollment:   2000,  // slider max — API treats 2000 as 2000+
  minLunchPct:     0,
  maxLunchPct:     100,
  categories:   [],
  states:       [],
  grades:       [],
  gradeGroupLabels: []
};

const YELLOW = "#fbbf24";
const AMBER  = "#f97316";
const RED    = "#dc2626";
const CREAM  = "#fffbf5";

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 800, color: "#dc2626",
      textTransform: "uppercase", letterSpacing: "1px",
      marginBottom: 8, marginTop: 18,
      display: "flex", alignItems: "center", gap: 4,
    }}>
      {children}
    </div>
  );
}


// Override pointer-events via inline style per thumb using a wrapper trick.
// Actually the cleanest fix is to use pointer-events on the thumb pseudo-element only.
// We handle this in the <style> block below.

function Chip({ label, active, onClick, color = RED }) {
  return (
    <button onClick={onClick} style={{
      padding: "4px 10px", borderRadius: 999,
      border:  active ? `2px solid ${color}` : "2px solid #fde68a",
      cursor:  "pointer", fontSize: 11, fontWeight: 700,
      background: active ? color : "#fff9e6",
      color:      active ? "#fff" : "#92400e",
      transition: "all .12s", whiteSpace: "nowrap",
    }}>
      {label}
    </button>
  );
}

export default function MapFilterPanel({ onFiltersChange, stats }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  const update = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleArray = useCallback((key, value) => {
    setFilters(prev => {
      const arr = prev[key];
      const nextArr = arr.includes(value) 
        ? arr.filter(v => v !== value) 
        : [...arr, value];
      return { ...prev, [key]: nextArr };
    });
  }, []);
  const hasActive = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);
  const reset = () => setFilters(DEFAULT_FILTERS);

  return (
    <div style={{
      width: 300, flexShrink: 0,
      background: CREAM, borderRight: "3px solid #fde68a",
      overflowY: "auto", fontFamily: "'Segoe UI', system-ui, sans-serif",
      position: "relative",
    }}>
      {/* Ruled lines — behind content */}
      {[...Array(30)].map((_, i) => (
        <div key={i} style={{ position: "absolute", left: 0, right: 0, top: 40 + i * 30, height: 1, background: "rgba(147,197,253,0.2)", pointerEvents: "none" }} />
      ))}
 
      <div style={{ padding: "16px 16px 80px 8px", position: "relative", borderLeft: "2px solid rgba(252,165,165,0.5)", marginLeft: 48 }}>

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 4, paddingBottom: 12, borderBottom: "2px dashed #fde68a",
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#dc2626" }}>🎯 Filter</div>
            {/* {stats && (
              <div style={{ fontSize: 11, color: "#92400e", marginTop: 1 }}>
                {stats.totalShown} of {stats.totalAll} teachers
              </div>
            )} */}
          </div>
          {hasActive && (
            <button onClick={reset} style={{
              fontSize: 11, fontWeight: 800, color: "#dc2626",
              background: "#fef2f2", border: "2px solid #fca5a5",
              borderRadius: 999, padding: "3px 10px", cursor: "pointer",
            }}>
              Reset ✕
            </button>
          )}
        </div>

        {/* Wishlist Total */}
        <SectionTitle>💰 Wishlist Total</SectionTitle>
        <DualRangeSlider
          min={0} max={500} step={25}
          valueMin={filters.minValue}   valueMax={filters.maxValue}
          onChangeMin={v => update("minValue", v)}
          onChangeMax={v => update("maxValue", v)}
          format={v => `$${v.toLocaleString()}`}
        />

        {/* Item Price */}
        <SectionTitle>🏷️ Individual Item Price</SectionTitle>
        <DualRangeSlider
          min={0} max={200} step={5}
          valueMin={filters.itemMinPrice} valueMax={filters.itemMaxPrice}
          onChangeMin={v => update("itemMinPrice", v)}
          onChangeMax={v => update("itemMaxPrice", v)}
          format={v => `$${v}`}
        />

        {/* Items Needed */}
        <SectionTitle>🛒 # Items Still Needed</SectionTitle>
        <DualRangeSlider
          min={1} max={100} step={1}
          valueMin={filters.minNeeded} valueMax={filters.maxNeeded}
          onChangeMin={v => update("minNeeded", v)}
          onChangeMax={v => update("maxNeeded", v)}
          format={v => `${v}`}
        />
        <SectionTitle>🏫 School Size (Students)</SectionTitle>
        <DualRangeSlider
          min={0} max={2000} step={50}
          valueMin={filters.minEnrollment} valueMax={filters.maxEnrollment}
          onChangeMin={v => update("minEnrollment", v)}
          onChangeMax={v => update("maxEnrollment", v)}
          format={v => v.toLocaleString()}
        />
 
        {/* Free & Reduced Lunch % */}
        <SectionTitle>🍎 Free & Reduced Lunch %</SectionTitle>
        <DualRangeSlider
          min={0} max={100} step={5}
          valueMin={filters.minLunchPct} valueMax={filters.maxLunchPct}
          onChangeMin={v => update("minLunchPct", v)}
          onChangeMax={v => update("maxLunchPct", v)}
          format={v => `${v}%`}
        />

        {/* Category */}
        <SectionTitle>📦 Category</SectionTitle>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {CATEGORIES.map(({ id, emoji }) => (
            <Chip key={id} label={`${emoji} ${id}`}
              active={filters.categories.includes(id)}
              onClick={() => toggleArray("categories", id)} />
          ))}
        </div>

        {/* Grade */}
        <SectionTitle>🎓 Grade</SectionTitle>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {GRADE_GROUPS.map(group => {
            const active = (filters.gradeGroupLabels || []).includes(group.label);
            return (
              <Chip
                key={group.label}
                label={group.label}
                active={active}
                color={AMBER}
                onClick={() => {
                  setFilters(prev => {
                    const currentLabels = prev.gradeGroupLabels || [];
                    const isActive = currentLabels.includes(group.label);
                    const nextLabels = isActive
                      ? currentLabels.filter(l => l !== group.label)
                      : [...currentLabels, group.label];

                    // Recompute flat grade codes as the union of all selected groups
                    const nextGrades = [...new Set(
                      nextLabels.flatMap(l => GRADE_GROUPS.find(g => g.label === l).grades)
                    )];

                    return { ...prev, gradeGroupLabels: nextLabels, grades: nextGrades };
                  });
                }}
              />
            );
          })}
        </div>

      </div>

      <style>{`
        input[type=range] {
          pointer-events: none;
        }
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          pointer-events: all;
          width: 20px; height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f97316, #dc2626);
          border: 3px solid #fff;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(220,38,38,0.35);
        }
        input[type=range]::-moz-range-thumb {
          pointer-events: all;
          width: 20px; height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f97316, #dc2626);
          border: 3px solid #fff;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}